/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import {
  Document,
  Table,
  TableRow,
  PageOrientation,
  WidthType,
  TableLayoutType,
  Header,
} from 'docx';
import {
  paragraphWithBoldText,
  paragraph,
  heading,
  spacer,
  Appendix,
  Styles,
  ArrayToTableCellType,
  cellToTableCell,
  imageWrapper,
  getWWGLogoLandscape,
  pagebreak,
  getLogo,
  footerLogos,
  convertImgToBase64URL,
  getBorders
} from './document-structure';
import { formatDate } from '../../utils/date';
import { ReportData } from '../../types/reportData';
import { renderQuestionValue, renderTable } from './questionView';
import { getGroup, getSubGroup, Group, standards } from '@g17eco/core';
import { declarativeStyles } from './styles';
import { naturalSort } from '../../utils';
import { UtrValueTypes } from '../../utils/universalTracker';
import { getLatestNote } from './group-builder';
import { NO_ANSWER_SUBMITTED } from './constants';
import { UnitConfig } from '@g17eco/types/survey';
import { ReportGeneratorFn } from './types';
import { QUESTION } from '@constants/terminology';

type SubGroupsType = {
  [key: string]: ReportData[]
}

type GroupsType = {
  [key: string]: SubGroupsType;
};

const groupTitleStyle = (group: Group | undefined) => ({
  style: 'bold',
  textRun: {
    color: group?.colour ?? '',
    size: 26,
  },
});

const tableHeaderRowStyle = (group: Group | undefined) => ({
  style: 'bold',
  shading: {
    fill: group?.colour ?? '',
  },
  textRun: {
    color: 'FFFFFF',
  },
});

export const table = (data: TableRow[], size = 8640) => {
  const columnWidths = [2000, 3500, 5000, 3500];
  return new Table({
    rows: data,
    columnWidths: columnWidths,
    width: {
      size,
      type: WidthType.DXA,
    },
    layout: TableLayoutType.FIXED,
  })
}

const createCell = (cell: ArrayToTableCellType, styles?: Styles) => {
  return cellToTableCell(cell, { ...styles, borders: getBorders('6C757D')});
}

const getValueStr = (m: ReportData, unitConfig?: UnitConfig, displayUserInput?: boolean) => {
  if (m.universalTracker.valueType === UtrValueTypes.table) {
    const table = renderTable({ question: m, returnEmpty: false, unitConfig, displayUserInput });
    if (!table) {
      return;
    }
    return Appendix.add(m.universalTracker.name, table);
  }

  return renderQuestionValue(m, unitConfig, undefined, { displayUserInput });
};

const createRow = (currentData: ReportData, unitConfig?: UnitConfig, displayUserInput?: boolean) => {
  const companyResponse = getValueStr(currentData, unitConfig, displayUserInput) ?? NO_ANSWER_SUBMITTED;
  const code = getTypeCode(currentData);

  return new TableRow({
    children: [
      createCell(code),
      createCell(currentData.universalTracker.name),
      createCell(companyResponse ?? ''),
      createCell(getLatestNote(currentData)),
    ],
  });
}

const getTypeCode = (a: ReportData) => {
  if (a.universalTracker.alternatives?.gri) {
    return a.universalTracker.alternatives?.gri?.typeCode ?? '';
  }
  if (a.universalTracker.type === 'gri') {
    return a.universalTracker.typeCode;
  }
  return '';
}

const tableHeaderRow = (group: Group | undefined) =>
  new TableRow({
    children: [`${QUESTION.CAPITALIZED_SINGULAR} Code`, QUESTION.CAPITALIZED_SINGULAR, 'Response', 'Further Explanation / Notes'].map((title) =>
      createCell(title, tableHeaderRowStyle(group))
    ),
  });

const getGroupData = (data: ReportData[]) => {
  const dataGroups: GroupsType = {};

  data.forEach(d => {
    const typeCode = getTypeCode(d);
    if (typeCode === '') {
      return;
    }
    const prefix = 'gri-'; // @TODO should be replaced to use typeTags
    const group = prefix + typeCode[0];
    const subGroup = group + '-' + typeCode.substr(0, 3);
    if (!dataGroups[group]) {
      dataGroups[group] = {};
    }
    if (!dataGroups[group][subGroup]) {
      dataGroups[group][subGroup] = [];
    }
    dataGroups[group][subGroup].push(d);
  });
  return dataGroups;
}

const getTableRows = (subGroups: SubGroupsType, unitConfig?: UnitConfig, displayUserInput?: boolean) => {
  const rows: TableRow[] = [];
  Object.keys(subGroups)
    .sort()
    .forEach((subGroupKey) => {
      const d = subGroups[subGroupKey];
      d.sort((a, b) => naturalSort(getTypeCode(a), getTypeCode(b))).forEach((r) => rows.push(createRow(r, unitConfig, displayUserInput)));
    });
  return rows;
};

const getGroupPages = (dataGroups: GroupsType, unitConfig?: UnitConfig, displayUserInput?: boolean) => {
  const griGroup = getGroup('standards', 'gri') as Group;
  return Object.keys(dataGroups)
    .sort()
    .map((groupKey) => {
      const subGroups = dataGroups[groupKey];
      const group = getSubGroup(griGroup, groupKey);
      const rows: TableRow[] = getTableRows(subGroups, unitConfig, displayUserInput);
      return [
        paragraph(group?.name ?? '', groupTitleStyle(group)),
        table([tableHeaderRow(group), ...rows], 14000),
        pagebreak(),
      ];
    });
};

export const GRIReportGenerator: ReportGeneratorFn<Document> = async (
  survey,
  reportData,
  downloadScope,
) => {
  const clientLogoBase64 = survey.initiative.profile ? await convertImgToBase64URL(survey.initiative.profile) : undefined;

  const wwgLogoLandscape = await getWWGLogoLandscape();

  Appendix.init();
  const { displayUserInput } = downloadScope;

  const dataGroups = getGroupData(reportData);
  const periodCovered = formatDate(survey.effectiveDate, 'MMMM YYYY')
  const griLogoBase64 = await convertImgToBase64URL(standards.gri.src);

  const document = new Document({
    styles: {
      paragraphStyles: declarativeStyles
    },
    sections: [
      {
        properties: {
          titlePage: true,
          page: {
            size: {
              orientation: PageOrientation.LANDSCAPE,
            }
          }
        },
        headers: {
          first: new Header({
            children: [imageWrapper(getLogo(griLogoBase64, 60, 60)), imageWrapper(wwgLogoLandscape)],
          }),
        },
        footers: footerLogos(griLogoBase64, clientLogoBase64),
        children: [
          heading('GRI Aligned Report'),
          paragraphWithBoldText('Company Name: ', survey.initiative.name),
          paragraphWithBoldText('Company Sector: ', survey.initiative.industryText ?? ''),
          paragraphWithBoldText('Report Date: ', periodCovered),
          spacer(),
          ...getGroupPages(dataGroups, survey.unitConfig, displayUserInput).flat(),
          ...Appendix.render()
        ]
      }
    ]
  })

  return document;
}

